import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DevImpersonationService } from './dev-impersonation.service';
import { FEATURE_FLAG_CONFIGS, FeatureFlagConfig } from '../_config/feature-flag.constants';
import { isFeatureFlagHostAllowed } from '../_config/feature-flag-host';
import { getPortalViewFromAttributes } from '../_config/portal-view';
import { ConfigService } from '../config/config.service';

@Injectable({
	providedIn: 'root'
})
export class FeatureFlagService {
	private runtimeConfigs: { [featureName: string]: FeatureFlagConfig } = {};

	constructor(
		private devImpersonationService: DevImpersonationService,
		private configService: ConfigService
	) {
		// Subscribe to impersonation token changes to invalidate cache
		this.devImpersonationService.token$.subscribe(() => {
			// Clear runtime configs when impersonation changes (they may be user-specific)
			this.runtimeConfigs = {};
		});
	}

	/**
	 * Get config for a feature (from runtime override or constants)
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @returns FeatureFlagConfig or null if not found
	 */
	private getConfig(featureName: string): FeatureFlagConfig | null {
		const runtimeConfig = this.runtimeConfigs[featureName];
		if (runtimeConfig) {
			return runtimeConfig;
		}
		return FEATURE_FLAG_CONFIGS[featureName] || null;
	}

	/**
	 * Set allowed roles for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param roles Array of allowed roles
	 */
	setAllowedRolesForFeature(featureName: string, roles: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].roles = roles || [];
	}

	/**
	 * Set allowed emails for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param emails Array of allowed emails
	 */
	setAllowedEmailsForFeature(featureName: string, emails: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].emails = emails || [];
	}

	/**
	 * Set allowed client IDs for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param ids Array of allowed client IDs
	 */
	setAllowedClientIdsForFeature(featureName: string, ids: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].clientIds = ids || [];
	}

	/**
	 * Set allowed market IDs for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param ids Array of allowed market IDs
	 */
	setAllowedMarketIdsForFeature(featureName: string, ids: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].marketIds = ids || [];
	}

	/**
	 * Set allowed campus IDs for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param ids Array of allowed campus IDs
	 */
	setAllowedCampusIdsForFeature(featureName: string, ids: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].campusIds = ids || [];
	}

	/**
	 * Set allowed distributor IDs for a feature at runtime (e.g., from API)
	 * This overrides the constants file configuration
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @param ids Array of allowed distributor IDs
	 */
	setAllowedDistributorIdsForFeature(featureName: string, ids: string[]): void {
		if (!this.runtimeConfigs[featureName]) {
			this.runtimeConfigs[featureName] = {};
		}
		this.runtimeConfigs[featureName].distributorIds = ids || [];
	}

	/**
	 * Primary generalized method to check if user has access to a feature flag
	 * Host allowlist is a pre-filter when configured.
	 * If an email allowlist is present, email must match first (only for emailRoles, when those are set).
	 * Then checks in priority order: roles -> portalViews -> clientIds -> marketIds -> campusIds -> distributorIds
	 * @param featureName The name of the feature (e.g., 'orders')
	 * @returns true if user has access, false otherwise
	 */
	hasFeatureFlag(featureName: string): boolean {
		const config = this.getConfig(featureName);

		if (!config) {
			return false;
		}

		if (!isFeatureFlagHostAllowed(config.hosts, this.getCurrentHost())) {
			return false;
		}

		const userAttr = this.devImpersonationService.getUserAttributes();

		if (!userAttr) {
			return false;
		}

		const userRole = userAttr['custom:role'];
		const emailAttr = userAttr['email'] || userAttr['custom:email'];
		const email = emailAttr ? String(emailAttr).toLowerCase() : null;
		const clientId = userAttr['custom:client_id'];
		const marketId = userAttr['custom:market_id'];
		const campusId = userAttr['custom:campus_id'];
		const distiId = userAttr['custom:disti_id'];

		// Required pre-filter: if emails are configured, user email must be allowed
		const emailsApply = !config.emailRoles || config.emailRoles.length === 0 || config.emailRoles.indexOf(userRole) !== -1;
		if (config.emails && config.emails.length > 0 && emailsApply) {
			if (!email) {
				return false;
			}

			const normalizedAllowedEmails = config.emails.map((allowedEmail: string) => String(allowedEmail).toLowerCase());
			if (normalizedAllowedEmails.indexOf(email) === -1) {
				return false;
			}
		}

		// Priority 1: Check allowed roles
		if (config.roles && config.roles.length > 0 && userRole) {
			if (config.roles.includes(userRole)) {
				return true;
			}
		}

		// Priority 1.5: Check allowed portal views
		if (config.portalViews && config.portalViews.length > 0) {
			if (config.portalViews.includes(getPortalViewFromAttributes(userAttr))) {
				return true;
			}
		}

		// Priority 2: Check allowed client IDs
		if (config.clientIds && config.clientIds.length > 0 && clientId) {
			if (config.clientIds.includes(clientId)) {
				return true;
			}
		}

		// Priority 2.5: Check allowed market IDs
		if (config.marketIds && config.marketIds.length > 0 && marketId) {
			if (config.marketIds.includes(marketId)) {
				return true;
			}
		}

		// Priority 3: Check allowed campus IDs
		if (config.campusIds && config.campusIds.length > 0 && campusId) {
			if (config.campusIds.includes(campusId)) {
				return true;
			}
		}

		// Priority 4: Check allowed distributor IDs
		if (config.distributorIds && config.distributorIds.length > 0 && distiId) {
			if (config.distributorIds.includes(distiId)) {
				return true;
			}
		}

		// Default: deny access
		return false;
	}

	/**
	 * Convenience method to check orders access
	 * @returns true if user has access to orders, false otherwise
	 */
	canAccessOrders(): boolean {
		return this.hasFeatureFlag('orders');
	}

	canAccessVouchers(): boolean {
		return this.hasFeatureFlag('vouchers');
	}

	canAccessFreeMode(): boolean {
		return this.hasFeatureFlag('free_mode');
	}

	/**
	 * Reactive check for orders access
	 * Observes DevImpersonationService.token$ for changes
	 * @returns Observable<boolean> that emits true if user has access to orders
	 */
	canAccessOrders$(): Observable<boolean> {
		return combineLatest([
			this.devImpersonationService.token$
		]).pipe(
			map(() => this.canAccessOrders())
		);
	}

	private getCurrentHost(): string {
		if (!this.configService.isLoaded()) {
			return '';
		}
		return String(this.configService.get('host') || '');
	}
}
